/**
 * Expiring Batch Details Dialog
 * Displays detailed information about an expiring batch
 * Uses data directly from ExpiringBatch object (no API call)
 */

import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { AlertTriangle, Calendar, Package, Droplets, Award } from 'lucide-react';
import { ExpiringBatch } from './ExpiringBatchesAlertTab';

interface ExpiringBatchDetailsDialogProps {
  batch: ExpiringBatch | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const ExpiringBatchDetailsDialog: React.FC<ExpiringBatchDetailsDialogProps> = ({
  batch,
  open,
  onOpenChange,
}) => {
  if (!batch) return null;

  const getUrgencyColor = (level: string) => {
    switch (level) {
      case 'CRITICAL':
        return 'bg-red-100 text-red-800 border-red-300';
      case 'HIGH':
        return 'bg-yellow-100 text-yellow-800 border-yellow-300';
      case 'MEDIUM':
        return 'bg-green-100 text-green-800 border-green-300';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-300';
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <Package className="w-5 h-5" />
            Batch Details: {batch.batchNumber}
          </DialogTitle>
          <DialogDescription>
            Complete information for this expiring batch
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Urgency Alert */}
          {batch.urgencyLevel === 'CRITICAL' && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="font-semibold text-red-900">Critical - Immediate Action Required</h4>
                <p className="text-sm text-red-700 mt-1">
                  This batch expires very soon ({batch.daysRemaining} days remaining).
                  Consider creating a discount order or marking for disposal.
                </p>
              </div>
            </div>
          )}

          {/* Basic Information */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Basic Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm text-gray-600 font-medium">Batch Number</label>
                  <p className="text-lg font-semibold text-blue-600">{batch.batchNumber}</p>
                </div>
                <div>
                  <label className="text-sm text-gray-600 font-medium">Product Type</label>
                  <p className="text-lg font-semibold">{batch.productType}</p>
                </div>
                <div>
                  <label className="text-sm text-gray-600 font-medium">Production Date</label>
                  <p className="text-sm font-semibold">
                    {new Date(batch.productionDate).toLocaleDateString()}
                  </p>
                </div>
                <div>
                  <label className="text-sm text-gray-600 font-medium">Storage Location</label>
                  <p className="text-sm font-semibold">{batch.storageLocation}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Expiry Information */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                Expiry Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm text-gray-600 font-medium">Expiry Date</label>
                  <p className="text-lg font-semibold">
                    {new Date(batch.expiryDate).toLocaleDateString()}
                  </p>
                </div>
                <div>
                  <label className="text-sm text-gray-600 font-medium">Days Remaining</label>
                  <div className="flex items-center gap-2">
                    <span className={`text-lg font-bold px-3 py-1 rounded ${
                      batch.daysRemaining < 0 ? 'bg-red-100 text-red-800' :
                      batch.daysRemaining < 7 ? 'bg-red-100 text-red-800' :
                      batch.daysRemaining < 15 ? 'bg-yellow-100 text-yellow-800' :
                      'bg-green-100 text-green-800'
                    }`}>
                      {batch.daysRemaining < 0 ? 'Expired' : batch.daysRemaining}
                    </span>
                  </div>
                </div>
              </div>
              <div>
                <label className="text-sm text-gray-600 font-medium">Urgency Status</label>
                <Badge className={`mt-2 ${getUrgencyColor(batch.urgencyLevel)}`}>
                  {batch.urgencyLevel === 'CRITICAL' && '🔴 Critical'}
                  {batch.urgencyLevel === 'HIGH' && '🟡 High (Warning)'}
                  {batch.urgencyLevel === 'MEDIUM' && '🟢 Medium (Normal)'}
                  {batch.urgencyLevel === 'LOW' && '⚪ Low'}
                </Badge>
              </div>
            </CardContent>
          </Card>

          {/* Quantity Information */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Droplets className="w-4 h-4" />
                Quantity Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm text-gray-600 font-medium">Available Quantity</label>
                  <p className="text-lg font-semibold text-green-600">
                    {batch.availableQuantity.toLocaleString()} {batch.quantityUnit}
                  </p>
                </div>
                <div>
                  <label className="text-sm text-gray-600 font-medium">Current Quantity</label>
                  <p className="text-lg font-semibold">
                    {batch.currentQuantity.toLocaleString()} {batch.quantityUnit}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Quality Information */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Award className="w-4 h-4" />
                Quality Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm text-gray-600 font-medium">Quality Grade</label>
                  <Badge variant="outline" className="mt-2">{batch.qualityGrade}</Badge>
                </div>
                <div>
                  <label className="text-sm text-gray-600 font-medium">Purity Percentage</label>
                  <p className="text-lg font-semibold text-blue-600">
                    {batch.purityPercentage}%
                  </p>
                </div>
              </div>
              <div>
                <label className="text-sm text-gray-600 font-medium">PESO Compliance</label>
                <div className="flex items-center gap-2 mt-2">
                  <Badge variant={batch.pesoCompliant ? 'default' : 'secondary'}>
                    {batch.pesoCompliant ? '✅ Compliant' : '⏳ Pending'}
                  </Badge>
                  {batch.pesoCertificateNumber && (
                    <span className="text-sm text-gray-600">
                      Cert: {batch.pesoCertificateNumber}
                    </span>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Action Summary */}
          <Card className="bg-blue-50 border-blue-200">
            <CardContent className="pt-6">
              <h4 className="font-semibold mb-2">Recommended Actions</h4>
              <ul className="text-sm space-y-1 text-blue-900">
                {batch.daysRemaining < 7 && (
                  <>
                    <li>✓ Create a discount order to accelerate sales</li>
                    <li>✓ Notify sales team for immediate action</li>
                    {batch.daysRemaining < 1 && <li>✓ Consider marking for disposal if not sold</li>}
                  </>
                )}
                {batch.daysRemaining >= 7 && batch.daysRemaining < 14 && (
                  <>
                    <li>✓ Monitor closely for expiry</li>
                    <li>✓ Prepare discount strategy if needed</li>
                  </>
                )}
                {batch.daysRemaining >= 14 && (
                  <li>✓ Monitor expiry date regularly</li>
                )}
              </ul>
            </CardContent>
          </Card>
        </div>
      </DialogContent>
    </Dialog>
  );
};
